import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { speak, playSuccessSound, playErrorSound, playStarSound } from '../utils/speech';

interface Props { onBack: () => void; age: 'little' | 'big'; }

const wordPool = [
  { arabic: 'قطة', emoji: '🐱' }, { arabic: 'كلب', emoji: '🐶' },
  { arabic: 'أسد', emoji: '🦁' }, { arabic: 'طائر', emoji: '🐦' },
  { arabic: 'سمكة', emoji: '🐟' }, { arabic: 'فيل', emoji: '🐘' },
  { arabic: 'شمس', emoji: '☀️' }, { arabic: 'نجمة', emoji: '⭐' },
];

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export const MemoryGame: React.FC<Props> = ({ onBack, age }) => {
  const count = age === 'little' ? 4 : 6;
  const pairs = wordPool.slice(0, count);

  const [cards] = useState(() =>
    shuffle([
      ...pairs.map((w, i) => ({ ...w, id: i * 2, type: 'arabic' as const, matched: false, flipped: false })),
      ...pairs.map((w, i) => ({ ...w, id: i * 2 + 1, type: 'emoji' as const, matched: false, flipped: false })),
    ])
  );
  const [flipped, setFlipped] = useState<number[]>([]);
  const [matched, setMatched] = useState<number[]>([]);
  const [moves, setMoves] = useState(0);
  const [done, setDone] = useState(false);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (matched.length === pairs.length) {
      setTimeout(() => { playStarSound(); setDone(true); }, 500);
    }
  }, [matched.length, pairs.length]);

  const handleFlip = useCallback((idx: number) => {
    if (locked) return;
    if (flipped.includes(idx) || matched.some(m => cards[m].id === cards[idx].id)) return;
    if (flipped.length === 2) return;

    const newFlipped = [...flipped, idx];
    setFlipped(newFlipped);

    const card = cards[idx];
    if (card.type === 'arabic') speak(card.arabic, 'ar');

    if (newFlipped.length === 2) {
      setMoves(m => m + 1);
      setLocked(true);
      const [a, b] = newFlipped;
      const cardA = cards[a];
      const cardB = cards[b];
      // Match if same arabic word
      if (cardA.arabic === cardB.arabic && cardA.type !== cardB.type) {
        playSuccessSound();
        setMatched(prev => [...prev, a, b]);
        setFlipped([]);
        setLocked(false);
      } else {
        playErrorSound();
        setTimeout(() => { setFlipped([]); setLocked(false); }, 1000);
      }
    }
  }, [flipped, matched, cards, locked]);

  const isFlipped = (idx: number) => flipped.includes(idx) || matched.includes(idx);

  const stars = moves <= pairs.length * 2 ? 3 : moves <= pairs.length * 3 ? 2 : 1;

  if (done) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '20px' }}>
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}>
        <div style={{ fontSize: '80px' }}>{'⭐'.repeat(stars)}</div>
        <h2 style={{ fontFamily: "'Fredoka One', cursive", fontSize: '2.5rem', color: '#FF6B6B', margin: '20px 0' }}>
          {stars === 3 ? 'رائع جداً! 🏆' : 'أحسنت! 🌟'}
        </h2>
        <p style={{ fontSize: '1.2rem', color: '#636E72', marginBottom: '30px' }}>
          You found all pairs in <strong>{moves}</strong> moves!
        </p>
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <motion.button whileTap={{ scale: 0.9 }} onClick={() => window.location.reload()}
            style={{ background: '#4ECDC4', border: 'none', borderRadius: '50px', padding: '15px 30px', color: 'white', fontFamily: "'Fredoka One', cursive", fontSize: '1.2rem', cursor: 'pointer' }}>
            Play Again 🔄
          </motion.button>
          <motion.button whileTap={{ scale: 0.9 }} onClick={onBack}
            style={{ background: 'white', border: '3px solid #4ECDC4', borderRadius: '50px', padding: '15px 30px', color: '#4ECDC4', fontFamily: "'Fredoka One', cursive", fontSize: '1.2rem', cursor: 'pointer' }}>
            Menu 🏠
          </motion.button>
        </div>
      </motion.div>
    </div>
  );

  const cols = age === 'little' ? 4 : 4;

  return (
    <div style={{ minHeight: '100vh', padding: '20px' }}>
      <div style={{ maxWidth: '700px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <motion.button whileTap={{ scale: 0.9 }} onClick={onBack}
            style={{ background: 'white', border: '3px solid #4ECDC4', borderRadius: '50px', padding: '10px 20px', cursor: 'pointer', color: '#4ECDC4', fontWeight: 700 }}>
            ← Back
          </motion.button>
          <h2 style={{ fontFamily: "'Fredoka One', cursive", color: '#4ECDC4', fontSize: '1.8rem' }}>Memory! 🧠</h2>
          <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: '1.1rem', color: '#4ECDC4' }}>
            👆 {moves} moves
          </div>
        </div>
        <p style={{ textAlign: 'center', color: '#636E72', marginBottom: '20px' }}>
          Find matching pairs! 🎴
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '12px' }}>
          {cards.map((card, idx) => (
            <motion.div key={card.id} onClick={() => handleFlip(idx)}
              whileHover={!isFlipped(idx) ? { scale: 1.05 } : {}}
              whileTap={!isFlipped(idx) ? { scale: 0.95 } : {}}
              style={{ aspectRatio: '1', cursor: isFlipped(idx) ? 'default' : 'pointer' }}>
              <motion.div animate={{ rotateY: isFlipped(idx) ? 180 : 0 }}
                transition={{ duration: 0.4 }}
                style={{ width: '100%', height: '100%', position: 'relative', transformStyle: 'preserve-3d' }}>
                {/* Front */}
                <div style={{
                  position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #6C63FF, #FF6B6B)',
                  borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '2rem', backfaceVisibility: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.15)'
                }}>🎴</div>
                {/* Back */}
                <div style={{
                  position: 'absolute', inset: 0,
                  background: matched.includes(idx) ? '#00B894' : card.type === 'arabic' ? '#6C63FF' : '#FF6B6B',
                  borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backfaceVisibility: 'hidden', transform: 'rotateY(180deg)',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.15)'
                }}>
                  {card.type === 'arabic'
                    ? <span style={{ color: 'white', fontSize: '1.3rem', direction: 'rtl', fontWeight: 700, padding: '5px' }}>{card.arabic}</span>
                    : <span style={{ fontSize: '2.5rem' }}>{card.emoji}</span>}
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};
